import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import {
  Add,
  ChatBubbleOutline,
  Close,
  EmojiEmotions,
  FormatBold,
  FormatItalic,
  StrikethroughS,
  AttachFile,
  Search,
  LibraryBooks,
} from "@mui/icons-material";
import { ColorsConstants } from "../../../cssUtils/constants";
import typography from "../../../cssUtils/typography";
import ButtonOne from "../../../common/normal-button";
import { ReactComponent as ChatPrimary } from "../../../assets/images/icons/chatbot_config/chat-primary.svg";
import { ReactComponent as Worflow } from "../../../assets/images/icons/chatbot_config/workflow.svg";

// ── Reusable Segmented Toggle ─────────────────────────────────────────────────
function SegmentedToggle({ options, value, onChange }) {
  return (
    <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
      {options.map((option) => (
        <Button
          key={option}
          onClick={() => onChange(option)}
          sx={{
            flex: 1,
            py: 1,
            px: 1,
            borderRadius: "8px",
            ...typography.bodySmall,
            fontWeight: value === option ? 600 : 500,
            textTransform: "none",
            backgroundColor: value === option ? "#F0F5FF" : "transparent",
            color:
              value === option
                ? ColorsConstants.primaryBackgroundColor
                : "#6C7C93",
            border:
              value === option
                ? `1.5px solid ${ColorsConstants.primaryBackgroundColor}`
                : "1px solid #DFE5EC",
            boxShadow: "none",
            whiteSpace: "nowrap",
            "&:hover": {
              backgroundColor: value === option ? "#F0F5FF" : "#F7F8FA",
              border:
                value === option
                  ? `1.5px solid ${ColorsConstants.primaryBackgroundColor}`
                  : "1px solid #B0BEC5",
            },
          }}
        >
          {option}
        </Button>
      ))}
    </Box>
  );
}

// ── Workflow Library Panel ────────────────────────────────────────────────────
const WORKFLOWS = [
  "Untitled Workflow 17715000925S5",
  "TestFlow",
  "ai_automated_technical_integration_management_1x",
  "ai_ecommerce_platform_selection_assistance_gp",
  "ai_omnichannel_customer_journey_optimization_oj",
  "ai_omnichannel_marketing_campaign_workflow_1l",
  "ai_ecommerce_platform_selection_assistance_5h",
];

function WorkflowLibraryPanel({ onSelect, selectedWorkflow }) {
  const [search, setSearch] = useState("");

  const filtered = WORKFLOWS.filter((w) =>
    w.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Box
      sx={{
        border: "1px solid #DFE5EC",
        borderRadius: "10px",
        overflow: "hidden",
        bgcolor: "#FFFFFF",
      }}
    >
      {/* Search */}
      <Box sx={{ p: 1.5, borderBottom: "1px solid #F0F2F8" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search a workflow"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              ...typography.bodySmall,
              bgcolor: "#F8FAFC",
              height: "49px",
              "& fieldset": { borderColor: "#DFE5EC" },
              "&:hover fieldset": { borderColor: "#B0BEC5" },
              "&.Mui-focused fieldset": {
                borderColor: ColorsConstants.primaryBackgroundColor,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 16, color: "#94A3B8" }} />
              </InputAdornment>
            ),
            sx: {
              ...typography.bodySmall,
              "&::placeholder": {
                ...typography.bodySmall,
                color: "#94A3B8",
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* List */}
      <Box>
        <Box sx={{ bgcolor: "#F8FAFC80", mb: 1 }}>
          <Typography
            sx={{
              ...typography.bodySmall,
              fontWeight: 600,
              color: "#94A3B8",
              letterSpacing: "0.06em",
              px: 2,
              pt: 1.5,
              pb: 1.5,
              textTransform: "uppercase",
            }}
          >
            All Workflows
          </Typography>
        </Box>
        <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
          {filtered.map((wf) => (
            <Box
              key={wf}
              onClick={() => onSelect(wf)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                cursor: "pointer",
                bgcolor: selectedWorkflow === wf ? "#F0F5FF" : "transparent",
                "&:hover": {
                  bgcolor: selectedWorkflow === wf ? "#E8F0FF" : "#F7F9FC",
                },
              }}
            >
              {/* Workflow icon */}
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "7px",
                  bgcolor: "#EEF4FF",
                  border: "1px solid #D5E4FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Worflow
                  sx={{
                    fontSize: 15,
                    color: ColorsConstants.primaryBackgroundColor,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color:
                    selectedWorkflow === wf
                      ? ColorsConstants.primaryBackgroundColor
                      : "#1D2330",
                  fontWeight: selectedWorkflow === wf ? 500 : 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {wf}
              </Typography>
            </Box>
          ))}
          {filtered.length === 0 && (
            <Typography
              sx={{
                ...typography.bodySmall,
                color: "#9AA6B8",
                px: 2,
                py: 2,
                textAlign: "center",
              }}
            >
              No workflows found.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Create Chatbot Start Point Modal ─────────────────────────────────────────
export default function CreateChatbotModal({ open, onClose }) {
  const [triggerType, setTriggerType] = useState("Exact Match");
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [responseAction, setResponseAction] = useState("Custom Message");
  const [messageBody, setMessageBody] = useState("");
  const [formButtonText, setFormButtonText] = useState("");
  const [selectedForm, setSelectedForm] = useState("");
  const [actionOnOpening, setActionOnOpening] = useState(
    "Navigate to first screen",
  );
  const [showWorkflowLibrary, setShowWorkflowLibrary] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");

  const maxMessageLength = 1024;
  const maxKeywordLength = 100;

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" && keywordInput.trim()) {
      e.preventDefault();
      if (!keywords.includes(keywordInput.trim())) {
        setKeywords((prev) => [...prev, keywordInput.trim()]);
      }
      setKeywordInput("");
    }
  };

  const handleDeleteKeyword = (kw) => {
    setKeywords((prev) => prev.filter((k) => k !== kw));
  };

  const handleResponseActionChange = (val) => {
    setResponseAction(val);
    if (val !== "Workflow") {
      setShowWorkflowLibrary(false);
      setSelectedWorkflow("");
    }
  };

  const handleClose = () => {
    setTriggerType("Exact Match");
    setKeywords([]);
    setKeywordInput("");
    setResponseAction("Custom Message");
    setMessageBody("");
    setFormButtonText("");
    setSelectedForm("");
    setActionOnOpening("Navigate to first screen");
    setShowWorkflowLibrary(false);
    setSelectedWorkflow("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: "8px",
          border: "1px solid #E5E7EB",
          maxWidth: 580,
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          backgroundColor: "#F0F7FF",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChatPrimary
            sx={{ fontSize: 18, color: ColorsConstants.primaryPurple100 }}
          />
          <Typography sx={{ ...typography.h2, color: "#1D2330" }}>
            Create Chatbot Start Point
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{
            color: "#6C7C93",
            borderRadius: "6px",
            "&:hover": { bgcolor: "#E4EBF5", color: "#1D2330" },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent
        sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 3 }}
      >
        {/* Trigger */}
        <Box>
          <Typography
            sx={{
              ...typography.h2,
              color: "#1D2330",
              mb: 0.5,
              mt: 2,
            }}
          >
            Trigger
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, color: "#6C7C93", mb: 1.5 }}>
            How would you like to trigger the automation?
          </Typography>
          <SegmentedToggle
            options={["Exact Match", "Contains", "Any Keyword"]}
            value={triggerType}
            onChange={setTriggerType}
          />
        </Box>

        {/* Keywords */}
        <Box>
          <Typography
            sx={{ ...typography.bodyLarge, fontWeight: 500, color: "#1D2330", mb: 1 }}
          >
            Enter the keywords that trigger this flow
          </Typography>

          <Box
            sx={{
              border: "1px solid #DFE5EC",
              borderRadius: "8px",
              p: 1.5,
              bgcolor: "#FAFAFA",
              minHeight: 56,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              cursor: "text",
            }}
            onClick={() => document.getElementById("keyword-input")?.focus()}
          >
            {keywords.map((kw) => (
              <Chip
                key={kw}
                label={kw}
                size="small"
                onDelete={() => handleDeleteKeyword(kw)}
                sx={{
                  ...typography.bodySmall,
                  height: 28,
                  bgcolor: "#EEF4FF",
                  color: ColorsConstants.primaryPurple100,
                  border: `1px solid ${ColorsConstants.primaryPurple100}30`,
                  "& .MuiChip-deleteIcon": {
                    fontSize: 16,
                    color: ColorsConstants.primaryPurple100,
                    "&:hover": { color: "#E05252" },
                  },
                }}
              />
            ))}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                minWidth: 150,
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <input
                id="keyword-input"
                value={keywordInput}
                onChange={(e) =>
                  e.target.value.length <= maxKeywordLength &&
                  setKeywordInput(e.target.value)
                }
                onKeyDown={handleKeywordKeyDown}
                placeholder={
                  keywords.length === 0 ? "Type a keyword and press Enter" : ""
                }
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "14px",
                  color: "#1D2330",
                  padding: "8px 4px",
                  flex: 1,
                  minWidth: 100,
                  fontFamily: "inherit",
                }}
              />
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color: "#9AA6B8",
                  pr: 0.5,
                  whiteSpace: "nowrap",
                  fontWeight: 400,
                }}
              >
                {keywordInput.length}/{maxKeywordLength}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
            <Typography sx={{ ...typography.bodySmall, color: "#9AA6B8" }}>
              Press
            </Typography>
            <Box
              sx={{
                border: "1px solid #DFE5EC",
                borderRadius: "4px",
                px: 0.75,
                py: 0.1,
                bgcolor: "#F7F8FA",
              }}
            >
              <Typography
                sx={{ ...typography.bodySmall, color: "#1D2330", fontWeight: 600 }}
              >
                Enter
              </Typography>
            </Box>
            <Typography sx={{ ...typography.bodySmall, color: "#9AA6B8" }}>
              to add tags
            </Typography>
          </Box>
        </Box>

        {/* Response Action */}
        <Box>
          <Typography
            sx={{
              ...typography.h2,
              color: "#1D2330",
              mb: 0.5,
            }}
          >
            Response Action
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, color: "#6C7C93", mb: 1.5 }}>
            How would you like to trigger the automation?
          </Typography>
          <SegmentedToggle
            options={["Custom Message", "Workflow", "WhatsApp Form"]}
            value={responseAction}
            onChange={handleResponseActionChange}
          />
        </Box>

        {/* ── Custom Message ── */}
        {responseAction === "Custom Message" && (
          <Box>
            <Typography
              sx={{
                ...typography.bodyLarge,
                fontWeight: 500,
                color: "#1D2330",
                mb: 1,
              }}
            >
              Message body
            </Typography>
            <Box
              sx={{
                border: "1px solid #DFE5EC",
                borderRadius: "8px",
                bgcolor: "#FAFAFA",
                overflow: "hidden",
              }}
            >
              <textarea
                value={messageBody}
                onChange={(e) =>
                  e.target.value.length <= maxMessageLength &&
                  setMessageBody(e.target.value)
                }
                placeholder="Add your message here"
                rows={5}
                style={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "14px",
                  color: "#1D2330",
                  padding: "12px 14px",
                  resize: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  lineHeight: 1.6,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  px: 1.5,
                  pb: 0.75,
                }}
              >
                <Typography sx={{ ...typography.bodySmall, color: "#9AA6B8" }}>
                  {messageBody.length}/{maxMessageLength}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1.25,
              }}
            >
              <Button
                startIcon={<Add sx={{ fontSize: 16, color:'#193876' }} />}
                size="small"
                sx={{
                  color: '#193876',
                  fontWeight: 500,
                  ...typography.bodySmall,
                  textTransform: "none",
                  p: 0,
                  minWidth: 0,
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Add variable
              </Button>
              <Box sx={{ display: "flex", gap: 0.25 }}>
                {[
                  {
                    icon: <EmojiEmotions sx={{ fontSize: 18 }} />,
                    title: "Emoji",
                  },
                  { icon: <FormatBold sx={{ fontSize: 18 }} />, title: "Bold" },
                  {
                    icon: <FormatItalic sx={{ fontSize: 18 }} />,
                    title: "Italic",
                  },
                  {
                    icon: <StrikethroughS sx={{ fontSize: 18 }} />,
                    title: "Strikethrough",
                  },
                  {
                    icon: <AttachFile sx={{ fontSize: 18 }} />,
                    title: "Attach",
                  },
                ].map((btn) => (
                  <IconButton
                    key={btn.title}
                    size="small"
                    title={btn.title}
                    sx={{
                      color: "#6C7C93",
                      borderRadius: "6px",
                      p: 0.5,
                      "&:hover": { bgcolor: "#F0F2F8", color: "#1D2330" },
                    }}
                  >
                    {btn.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 600,
                  color: "#1D2330",
                  mb: 1,
                }}
              >
                Buttons (optional)
              </Typography>
              <ButtonOne
                startIcon={<Add sx={{ fontSize: 16 }} />}
                variant="outlined"
                sx={typography.bodySmall}
              >
                Add Button
              </ButtonOne>
            </Box>
          </Box>
        )}

        {/* ── Workflow ── */}
        {responseAction === "Workflow" && (
          <Box>
            {!showWorkflowLibrary ? (
              /* View Workflow Library clickable button */
              <Box
                onClick={() => setShowWorkflowLibrary(true)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: "9px",
                  py: "9.5px",
                  borderRadius: "2px",
                  bgcolor: "#1B76D11A",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <LibraryBooks
                  sx={{
                    fontSize: 18,
                    color: ColorsConstants.primaryPurple100,
                  }}
                />

                <Typography
                  sx={{
                    ...typography.bodyLarge,
                    fontWeight: 500,
                    color: ColorsConstants.primaryPurple100,
                    userSelect: "none",
                  }}
                >
                  View Workflow library
                </Typography>
              </Box>
            ) : (
              /* Workflow Library search + list panel */
              <WorkflowLibraryPanel
                onSelect={(wf) => setSelectedWorkflow(wf)}
                selectedWorkflow={selectedWorkflow}
              />
            )}
          </Box>
        )}

        {/* ── WhatsApp Form ── */}
        {responseAction === "WhatsApp Form" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 500,
                  color: "#1D2330",
                  mb: 1,
                }}
              >
                Message body
              </Typography>
              <Box
                sx={{
                  border: "1px solid #DFE5EC",
                  borderRadius: "8px",
                  bgcolor: "#FAFAFA",
                  overflow: "hidden",
                }}
              >
                <textarea
                  value={messageBody}
                  onChange={(e) =>
                    e.target.value.length <= maxMessageLength &&
                    setMessageBody(e.target.value)
                  }
                  placeholder="Add your message here"
                  rows={3}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "14px",
                    color: "#1D2330",
                    padding: "12px 14px",
                    resize: "none",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    lineHeight: 1.6,
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    px: 1.5,
                    pb: 0.75,
                  }}
                >
                  <Typography sx={{ ...typography.bodySmall, color: "#9AA6B8" }}>
                    {messageBody.length}/{maxMessageLength}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Button
                  startIcon={<Add sx={{ fontSize: 16, color:"#193876" }} />}
                  size="small"
                  sx={{
                    color: '#193876',
                    fontWeight: 500,
                    ...typography.bodySmall,
                    textTransform: "none",
                    p: 0,
                    minWidth: 0,
                    "&:hover": {
                      bgcolor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Add variable
                </Button>
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 500,
                  color: "#1D2330",
                  mb: 1,
                }}
              >
                Form Button Text
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter text for the button"
                value={formButtonText}
                onChange={(e) => setFormButtonText(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    ...typography.bodyLarge,
                    bgcolor: "#F6F7F9",
                    "& fieldset": { borderColor: "#DCDFE5" },
                    "&:hover fieldset": { borderColor: "#B0BEC5" },
                    "&.Mui-focused fieldset": {
                      borderColor: ColorsConstants.primaryBackgroundColor,
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    ...typography.bodyLarge,
                    "&::placeholder": {
                      ...typography.bodyLarge,
                      color: "#676F7E",
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 500,
                  color: "#1D2330",
                  mb: 1,
                }}
              >
                Select Form
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                placeholder="Select Form Name"
                value={selectedForm}
                onChange={(e) => setSelectedForm(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    ...typography.bodyLarge,
                    bgcolor: "#F6F7F9",
                    "& fieldset": { borderColor: "#DCDFE5" },
                    "&:hover fieldset": { borderColor: "#B0BEC5" },
                    "&.Mui-focused fieldset": {
                      borderColor: ColorsConstants.primaryBackgroundColor,
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    ...typography.bodyLarge,
                    color: selectedForm ? "inherit" : "#676F7E",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em style={{ color: "#676F7E", fontStyle: "normal", ...typography.bodyLarge }}>
                    Select Form Name
                  </em>
                </MenuItem>
                {/* Add your form options here */}
                <MenuItem value="form1" sx={typography.bodyLarge}>Form 1</MenuItem>
                <MenuItem value="form2" sx={typography.bodyLarge}>Form 2</MenuItem>
                <MenuItem value="form3" sx={typography.bodyLarge}>Form 3</MenuItem>
              </TextField>
            </Box>

            <Box>
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: 500,
                  color: "#1D2330",
                  mb: 1,
                }}
              >
                Action on Opening Form
              </Typography>
              <SegmentedToggle
                options={["Navigate to first screen", "Data Exchange"]}
                value={actionOnOpening}
                onChange={setActionOnOpening}
              />
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid #DFE5EC",
          justifyContent: "flex-end",
        }}
      >
        <ButtonOne
          variant="contained"
          title="Submit"
          sx={{
            backgroundColor: ColorsConstants.primaryBlue100,
            px: 4,
            py: 1,
            ...typography.bodyLarge,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: ColorsConstants.primaryBlue100,
              opacity: 0.9,
            },
          }}
          onClick={handleClose}
        >
          Submit
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
}